import { observer } from "mobx-react";
import React, { useEffect, useRef, useState } from "react";
import PageTitle from "../../components/common/pages/PageTitle";
import { FilterValue } from "../../../models/Filters/FilterValue";
import { useNotification } from "../../../utils/NotificationsContext";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  CircularProgress,
  Container,
  Grid2,
  IconButton,
  Paper,
  Tooltip,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import HistoryIcon from "@mui/icons-material/History";
import FormTitle from "../../components/common/form/FormTitle";
import FormSmallTextField from "../../components/common/form/FormTextField";
import AppDropDown from "../../components/common/filter/AppDropDown";
import { useStores } from "../../../use-stores";
import { EmployeeRoles } from "../../../models/Employees/EmployeeRoles";
import AppSearchDropDown from "../../components/common/filter/AppSearchDropDown";
import { NewUser, User } from "../../../models/User";
import ActivityLogDrawer from "../../components/common/drawer/ActivityLogDrawer";

interface AddUserProps {
  customer?: FilterValue[];
  setLoadingForPopup?: (loading: boolean) => void;
  isNavigationNeeded?: boolean;
  onClose?: (user: NewUser) => void;
}

const AddUser: React.FC<AddUserProps> = ({
  customer,
  setLoadingForPopup,
  isNavigationNeeded = true,
  onClose,
}) => {
  const { userStore, customerStore } = useStores();
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const { showError, showSuccess } = useNotification();
  let customerTimer: string | number | NodeJS.Timeout | null | undefined = null;
  const inputCustomerNameTextRef = useRef<string>("");

  const queryParams = new URLSearchParams(location.search);
  const userID = queryParams.get("user-id") || 0;
  const [isLoading, setIsLoading] = useState(false);
  const [isActivityDrawerOpen, setIsActivityDrawerOpen] = useState(false);
  const [isActivityLoading, setIsActivityLoading] = useState(false);

  const activeAdmin: FilterValue[] = [
    { id: "-1", value: "Select" },
    { id: "1", value: "True" },
    { id: "0", value: "False" },
  ];

  const [, setSearchTrigger] = useState<number>(0);

  const handleError = (message?: string) => {
    showError(
      "Error",
      message || "Something went wrong! Please try again later."
    );
  };
  useEffect(() => {
    // setIsLoading(true);
    if (userID) {
      userStore
        .getUserDetailsById(Number(userID))
        .catch((err) => handleError(err?.response?.data?.message))
        .finally(() => setIsLoading(false));
    } else {
      userStore.clearUserDetails();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userID]);

  const isViewOnly = (): boolean => {
    return (
      userStore.hasPermission(EmployeeRoles.VIEW_USER) &&
      !userStore.hasPermission(EmployeeRoles.CREATE_USER) &&
      !userStore.hasPermission(EmployeeRoles.EDIT_USER)
    );
  };

  const isNoAccess = (): boolean => {
    return (
      !userStore.hasPermission(EmployeeRoles.VIEW_USER) &&
      !userStore.hasPermission(EmployeeRoles.CREATE_USER) &&
      !userStore.hasPermission(EmployeeRoles.EDIT_USER)
    );
  };

  const validateForm = () => {
    const user = userStore.userDetails;
    if (!user.customer_id || user.customer_id === -1) {
      showError("Validation Error", "Please add customer name.");
      return false;
    }
    if (!user.first_name || user.first_name === "") {
      showError("Validation Error", "First Name is required.");
      return false;
    }
    if (!user.email || isValidEmail(user.email) === undefined) {
      showError("Validation Error", "Email Id is required.");
      return false;
    }

    if (!user.phone || user.phone === "") {
      showError("Validation Error", "Phone is required.");
      return false;
    }

    return true;
  };

  const isValidEmail = (email: string) => {
    const regex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,6}$/;
    return regex.test(email);
  };
  const saveDetails = () => {
    if (!validateForm()) return;
    if (
      userStore.userDetails.password &&
      userStore.userDetails.confirmPassword &&
      userStore.userDetails.password !== userStore.userDetails.confirmPassword
    ) {
      showError("Error", "Passwords does not match.");
      return;
    }

    if (userID) {
      updateUser();
    } else {
      createUser();
    }
  };

  const createUser = () => {
    setIsLoading(true);
    userStore
      .createUser({
        ...userStore.userDetails,
      })
      .then((res) => {
        if (setLoadingForPopup) {
          if (onClose) {
            onClose(res.result);
          }
        } else {
          showSuccess("User created successfully");
          navigate("/users/listing");
        }
        userStore.resetUser();
      })
      .catch((err) => {
        showError(
          "Error occurred",
          err?.response?.data?.message ||
            "Something went wrong! Please try again later."
        );
      })
      .finally(() => setIsLoading(false));
  };

  const updateUser = () => {
    userStore
      .updateUserDetails()
      .then((res) => {
        if (setLoadingForPopup) {
          if (onClose) {
            onClose(res?.result);
          }
        } else {
          setIsLoading(false);
          showSuccess("User updated successfully");
          navigate("/users/listing");
        }
      })
      .catch((err) => {
        showError(
          "Error occurred",
          err?.response?.data?.message ||
            "Something went wrong! Please try again later."
        );
      })
      .finally(() => setIsLoading(false));
  };

  const resetForm = () => {
    userStore.discardChanges();
  };

  const handleOpenActivityDrawer = () => {
    const resolvedUserId = Number(
      userStore.userDetails?.id ?? userID,
    );
    if (!resolvedUserId || Number.isNaN(resolvedUserId)) {
      showError("Error", "Unable to load activity history: user id is missing.");
      return;
    }
    setIsActivityDrawerOpen(true);
    setIsActivityLoading(true);
    userStore
      .getUserActivityLogs(resolvedUserId)
      .catch((err) =>
        showError(
          "Error",
          err?.response?.data?.message || "Failed to fetch activity logs",
        ),
      )
      .finally(() => setIsActivityLoading(false));
  };

  const searchCustomerName = (name: string) => {
    inputCustomerNameTextRef.current = name;
    setSearchTrigger((prev) => prev + 1);
    if (customerTimer) {
      clearTimeout(customerTimer);
    }

    if (name.length > 2) {
      customerTimer = setTimeout(() => {
        customerStore
          .searchCustomers({ page_no: 1, search_text: name })
          .catch((err) =>
            showError(
              "Error occurred",
              err?.response?.data?.message ||
                "Something went wrong! Please try again later."
            )
          );
      }, 700);
    }
  };

  const isCustomerFieldDisabled = () => {
    if (isViewOnly()) {
      return true;
    }

    if (
      userStore.originalUser.customer_id === undefined ||
      userStore.originalUser.customer_id === null
    ) {
      return false;
    }

    if (userStore.originalUser.customer_id === -1) {
      return false;
    }

    return true;
  };

  useEffect(() => {
    if (customer) {
      userStore.updateCustomerName(customer);
    }
  }, [customer]);

  return (
    <Container maxWidth="xl">
      {isNoAccess() ? (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          height="10rem"
          textAlign="center"
        >
          <Typography variant="h6" fontWeight="500" color="text.secondary">
            You do not have permission to view this section.
          </Typography>
        </Box>
      ) : (
        <>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <PageTitle title={userID ? "Update User" : "Create User"} />
            {userID && (
              <Tooltip title="Activity History">
                <IconButton
                  onClick={handleOpenActivityDrawer}
                  sx={{
                    backgroundColor: theme.palette.primary.main + "10",
                    color: theme.palette.primary.main,
                    "&:hover": {
                      backgroundColor: theme.palette.primary.main + "20",
                    },
                  }}
                >
                  <HistoryIcon />
                </IconButton>
              </Tooltip>
            )}
          </Box>
          {/* {isLoading ? (
            <Box
              sx={{
                position: "fixed",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <CircularProgress />
            </Box>
          ) : ( */}
          <Grid2 size={{ lg: 6, md: 6, xs: 6 }}>
            <Paper elevation={1} sx={{ padding: 4, mb: 4 }}>
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 2, mb: 1 }}
              >
                <Box sx={{ flex: 1 }}>
                  <FormTitle title="Select Customer Name" fontSize="12px" />
                  <AppSearchDropDown
                    required
                    isMultiSelect={false}
                    local={false}
                    fontSize="12px"
                    title="Select Customer Name"
                    value={
                      userStore.userDetails.customer_id
                        ? [
                            {
                              id: String(userStore.userDetails.customer_id),
                              value: userStore.userDetails.customer_name || "",
                            },
                          ]
                        : []
                    }
                    list={customerStore.customers.map((item) => {
                      return { id: String(item.id), value: item.name! };
                    })}
                    textFieldValue={inputCustomerNameTextRef.current || ""}
                    handleSearch={(name) => searchCustomerName(name!)}
                    disabled={isCustomerFieldDisabled()}
                    handleSelect={(value) => {
                      userStore.updateCustomerName(value);
                      if (inputCustomerNameTextRef.current) {
                        inputCustomerNameTextRef.current = "";
                      }
                    }}
                  />
                </Box>
                {/* <Box sx={{ flex: 1 }}>
                    <FormTitle title="Admin" fontSize="12px" />
                    <AppDropDown
                      title="Admin"
                      isMultiSelect={false}
                      required
                      value={
                        userStore.userDetails?.is_admin !== undefined
                          ? userStore.userDetails?.is_admin
                            ? "1"
                            : "0"
                          : "-1"
                      }
                      list={activeAdmin}
                      height="36px"
                      listHeight="30px"
                      disabled={isViewOnly()}
                      fontSize="12px"
                      onSingleSelectionChange={(id) => {
                        userStore.updateIsAdmin(id === "1");
                      }}
                    />
                  </Box> */}
              </Box>
              <Box sx={{ display: "none" }}>
                <FormSmallTextField
                  label="Customer Id"
                  required
                  variant="outlined"
                  color="secondary"
                  type="name"
                  fontSize="12px"
                  disabled={isViewOnly()}
                  value={userStore.userDetails.customer_id || ""}
                />
              </Box>
              <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                <Box sx={{ flex: 1 }}>
                  <FormTitle title="First Name" fontSize="12px" />
                  <FormSmallTextField
                    label="First Name"
                    required
                    variant="outlined"
                    color="secondary"
                    type="name"
                    fontSize="12px"
                    disabled={isViewOnly()}
                    value={userStore.userDetails.first_name || ""}
                    onChange={(id, value) =>
                      userStore.updateFirstName(String(value))
                    }
                  />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <FormTitle title="Last Name" fontSize="12px" />
                  <FormSmallTextField
                    label="Last Name"
                    variant="outlined"
                    color="secondary"
                    type="name"
                    disabled={isViewOnly()}
                    fontSize="12px"
                    value={userStore.userDetails.last_name || ""}
                    onChange={(id, value) =>
                      userStore.updateLastName(String(value))
                    }
                  />
                </Box>
              </Box>
              <Box
                sx={{ display: "flex", gap: 2, alignItems: "center", mt: -2 }}
              >
                <Box sx={{ flex: 1 }}>
                  <FormTitle title="Email" fontSize="12px" />
                  <FormSmallTextField
                    label="Add Email"
                    required
                    variant="outlined"
                    color="secondary"
                    disabled={!!userID || isViewOnly()}
                    type="email"
                    fontSize="12px"
                    value={userStore.userDetails.email || ""}
                    onChange={(id, value) =>
                      userStore.updateEmail(String(value))
                    }
                  />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <FormTitle
                    title="Phone Number(Start with +91)"
                    fontSize="12px"
                  />
                  <FormSmallTextField
                    label="+91XXXXXXXXX"
                    required
                    variant="outlined"
                    color="secondary"
                    type="tel"
                    disabled={isViewOnly()}
                    fontSize="12px"
                    value={userStore.userDetails.phone || ""}
                    onChange={(id, value) =>
                      userStore.updateIsPhoneNumber(String(value))
                    }
                  />
                </Box>
              </Box>

              {!userID && (
                <Box
                  sx={{
                    display: "flex",
                    gap: 2,
                    alignItems: "center",
                    mt: -2,
                  }}
                >
                  <Box sx={{ flex: 1 }}>
                    <FormTitle title="Password" fontSize="12px" />
                    <FormSmallTextField
                      label="Password"
                      required
                      variant="outlined"
                      color="secondary"
                      type="password"
                      fontSize="12px"
                      disabled={isViewOnly()}
                      value={userStore.userDetails.password || ""}
                      onChange={(id, value) =>
                        userStore.updatePassword(String(value))
                      }
                    />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <FormTitle title="Confirm Password" fontSize="12px" />
                    <FormSmallTextField
                      label="Confirm Password"
                      required
                      variant="outlined"
                      color="secondary"
                      type="password"
                      fontSize="12px"
                      disabled={isViewOnly()}
                      value={userStore.userDetails.confirmPassword || ""}
                      onChange={(id, value) =>
                        userStore.updateConfirmPassword(String(value))
                      }
                    />
                  </Box>
                </Box>
              )}
            </Paper>
            <Box
              sx={{
                display: isViewOnly() ? "none" : "flex",
                justifyContent: "space-around",
                mb: 10,
              }}
            >
              <Box sx={{ width: 1 / 3 }}>
                <Button
                  variant="outlined"
                  color="secondary"
                  sx={{ minHeight: "50px", padding: "6px 16px" }}
                  fullWidth
                  onClick={() => resetForm()}
                >
                  Reset
                </Button>
              </Box>
              <Box sx={{ width: 1 / 3 }}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => saveDetails()}
                  sx={{ minHeight: "50px", padding: "6px 16px" }}
                  fullWidth
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : (
                    "Save"
                  )}
                </Button>
              </Box>
            </Box>
          </Grid2>
          {/* )} */}
        </>
      )}
      <ActivityLogDrawer
        isOpen={isActivityDrawerOpen}
        onClose={() => setIsActivityDrawerOpen(false)}
        activityLogs={userStore.activityLogs}
        isLoading={isActivityLoading}
        title="Activity History"
        subtitle={userID ? `User #${userID}` : null}
      />
    </Container>
  );
};

export default observer(AddUser);
